import { useEffect, useState } from 'preact/hooks';
import { type ApiKeyCreated, type ApiKeySummary, DashboardApi, HttpError } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

export function Keys({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [keys, setKeys] = useState<ApiKeySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('My deploy bot');
  const [newKeyScopes, setNewKeyScopes] = useState<string>('agent:rw');
  const [newKeyCidrs, setNewKeyCidrs] = useState<string>('');
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  const refresh = async () => {
    try {
      const r = await api.listApiKeys(org.id);
      setKeys(r.keys);
      setError(null);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh closes over org.id only
  useEffect(() => {
    void refresh();
  }, [org.id]);

  const create = async () => {
    setCreating(true);
    try {
      const scopes = newKeyScopes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const cidrs = newKeyCidrs
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const body: { name: string; scopes?: string[]; allowedCidrs?: string[] } = {
        name: newKeyName.trim() || 'API key',
      };
      if (scopes.length > 0) body.scopes = scopes;
      if (cidrs.length > 0) body.allowedCidrs = cidrs;
      const created = await api.createApiKey(org.id, body);
      setCreatedKey(created);
      await refresh();
      setNewKeyName('My deploy bot');
      setNewKeyScopes('agent:rw');
      setNewKeyCidrs('');
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    if (!confirm('Revoke this key? Anything using it will start failing immediately.')) return;
    try {
      await api.revokeApiKey(org.id, id);
      await refresh();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const copyPlaintext = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopyState('copied');
    setTimeout(() => setCopyState('idle'), 2000);
  };

  return (
    <>
      <div class="page-header">
        <h1>API keys</h1>
        <p>
          Programmatic access for the embed script, the GitHub Action, and any custom integration.
          Treat keys like passwords — store them in a secrets manager, never commit to source
          control.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      {createdKey && (
        <div class="card key-created">
          <h2>New key — copy it now</h2>
          <p class="desc">
            This is the <strong>only time</strong> we show the plaintext. Store it somewhere safe
            before closing this dialog.
          </p>
          <div class="key-row">
            <code class="key-plaintext">{createdKey.plaintext}</code>
            <button type="button" onClick={() => void copyPlaintext(createdKey.plaintext)}>
              {copyState === 'copied' ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
          <button type="button" class="secondary" onClick={() => setCreatedKey(null)}>
            I've saved it
          </button>
        </div>
      )}

      <div class="card">
        <h2>Create a new key</h2>
        <div class="field">
          <label htmlFor="key-name">Name (for your own reference)</label>
          <input
            id="key-name"
            type="text"
            value={newKeyName}
            onInput={(e) => setNewKeyName((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label htmlFor="key-scopes">Scopes (comma-separated)</label>
          <input
            id="key-scopes"
            type="text"
            value={newKeyScopes}
            placeholder="agent:rw or describe,vpat,findings"
            onInput={(e) => setNewKeyScopes((e.target as HTMLInputElement).value)}
          />
          <p class="small muted">
            Use <code>agent:rw</code> for everything, or pick specific routes (<code>describe</code>
            , <code>vpat</code>, <code>findings</code>, etc.) for least-privilege keys.
          </p>
        </div>
        <div class="field">
          <label htmlFor="key-cidrs">IP allowlist (comma or space-separated CIDRs)</label>
          <input
            id="key-cidrs"
            type="text"
            value={newKeyCidrs}
            placeholder="optional — e.g. 10.0.0.0/8, 1.2.3.4/32"
            onInput={(e) => setNewKeyCidrs((e.target as HTMLInputElement).value)}
          />
          <p class="small muted">
            If set, requests from outside these ranges return <code>403 ip_not_allowed</code>.
          </p>
        </div>
        <button type="button" disabled={creating} onClick={() => void create()}>
          {creating ? 'Creating…' : 'Create key'}
        </button>
      </div>

      <div class="card">
        <h2>Existing keys</h2>
        {keys === null ? (
          <p class="muted">
            <output>Loading…</output>
          </p>
        ) : keys.length === 0 ? (
          <p class="muted">No keys yet. Create one above.</p>
        ) : (
          <table class="audit">
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Created</th>
                <th>Last used</th>
                <th>Status</th>
                <th>{/* actions */}</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td>
                    <code class="meta">{k.prefix}…</code>
                  </td>
                  <td>{new Date(k.createdAt).toLocaleDateString()}</td>
                  <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : '—'}</td>
                  <td>
                    {k.revokedAt ? (
                      <span
                        class="kind-pill"
                        style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
                      >
                        revoked
                      </span>
                    ) : (
                      <span
                        class="kind-pill"
                        style={{ borderColor: 'var(--success)', color: 'var(--success)' }}
                      >
                        active
                      </span>
                    )}
                  </td>
                  <td>
                    {!k.revokedAt && (
                      <button type="button" class="secondary" onClick={() => void revoke(k.id)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
